/** Guest notification settings (More → Notifications, Phase 5D).
 * Explicit opt-in, per-category preferences, iOS Home-Screen guidance.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { getPushEnv, getPushState, enablePush, disablePush } from "../lib/push";
import { Bell, BellOff, Smartphone, Share, PlusSquare, CheckCircle2 } from "lucide-react";
import { T } from "../lib/i18n";

const CATEGORIES = [
  { id: "announcements", label: "Announcements" },
  { id: "requests", label: "Requests & Help" },
  { id: "embroidery", label: "Embroidery" },
  { id: "music", label: "Music" },
  { id: "photo_queue", label: "Photo Queue" },
];

export default function NotificationSettings() {
  const [state, setState] = useState("loading");
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const env = getPushEnv();

  const refresh = async () => {
    setState(await getPushState());
    try {
      const { data } = await api.get("/push/status");
      setPrefs(data.prefs);
    } catch (e) { console.debug("push status failed", e); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enablePush();
      toast.success(T("Alerts enabled"));
      await refresh();
    } catch (e) {
      toast(e?.message || formatErr(e));
      await refresh();
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await disablePush();
      toast(T("Alerts turned off on this device"));
      await refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const setPref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      const { data } = await api.put("/push/prefs", { [key]: value });
      setPrefs(data.prefs);
    } catch (e) { toast.error(formatErr(e)); refresh(); }
  };

  return (
    <div className="space-y-3 fadein" data-testid="notification-settings">
      <h2 className="font-display text-2xl text-motif">{T("Wedding alerts")}</h2>
      <p className="text-sm text-motif-muted">
        {T("Get live updates for announcements, favors, songs, photos, and help — even when Venuefy is closed.")}
      </p>

      {state === "loading" && <div className="text-xs text-motif-muted">{T("Checking…")}</div>}

      {state === "ios_install" && (
        <div className="card-elev p-4" data-testid="push-ios-install">
          <div className="flex items-center gap-2 font-display text-lg text-motif mb-1">
            <Smartphone className="w-4 h-4 text-primary-motif" /> {T("Get wedding alerts")}
          </div>
          <p className="text-xs text-motif-muted mb-3">
            {T("Add Venuefy to your Home Screen to receive live wedding updates, favor notifications, and request status alerts.")}
          </p>
          <ol className="space-y-2 text-xs text-motif">
            <li className="flex items-center gap-2"><Share className="w-3.5 h-3.5 text-primary-motif flex-shrink-0" /> 1. {T("Tap Share")}</li>
            <li className="flex items-center gap-2"><PlusSquare className="w-3.5 h-3.5 text-primary-motif flex-shrink-0" /> 2. {T("Tap Add to Home Screen")}</li>
            <li className="flex items-center gap-2"><Smartphone className="w-3.5 h-3.5 text-primary-motif flex-shrink-0" /> 3. {T("Open Venuefy from your Home Screen")}</li>
            <li className="flex items-center gap-2"><Bell className="w-3.5 h-3.5 text-primary-motif flex-shrink-0" /> 4. {T("Tap Enable Notifications")}</li>
          </ol>
        </div>
      )}

      {state === "unsupported" && (
        <div className="card-elev p-4 text-xs text-motif-muted" data-testid="push-unsupported">
          {T("This browser does not support background notifications. In-app alerts still work while Venuefy is open.")}
        </div>
      )}

      {state === "denied" && (
        <div className="card-elev p-4" data-testid="push-denied">
          <div className="flex items-center gap-2 text-sm text-motif"><BellOff className="w-4 h-4 text-motif-muted" /> {T("Notifications are blocked in your browser settings.")}</div>
          <div className="text-[11px] text-motif-muted mt-1">{T("Allow notifications for this site in your browser settings, then return here.")}</div>
        </div>
      )}

      {state === "off" && (
        <div className="card-elev p-4" data-testid="push-off">
          <div className="flex items-center gap-2 font-display text-lg text-motif mb-2">
            <Bell className="w-4 h-4 text-primary-motif" /> {T("Alerts are off")}
          </div>
          <button onClick={enable} disabled={busy} className="btn-primary text-xs px-5 py-2.5" data-testid="push-settings-enable">
            {busy ? T("Enabling…") : T("Enable Alerts")}
          </button>
        </div>
      )}

      {state === "enabled" && (
        <>
          <div className="card-elev p-4" data-testid="push-enabled">
            <div className="flex items-center gap-2 text-sm text-motif">
              <CheckCircle2 className="w-4 h-4" style={{ color: "#10b981" }} /> {T("Alerts enabled on this device")}
            </div>
            <button onClick={disable} disabled={busy} className="btn-ghost text-[11px] mt-2 px-3 py-1.5" data-testid="push-settings-disable">
              {T("Turn off on this device")}
            </button>
          </div>

          {prefs && (
            <div className="card-elev p-4" data-testid="push-prefs">
              <ToggleRow
                label={T("Wedding Alerts")}
                strong
                checked={prefs.enabled !== false}
                onChange={(v) => setPref("enabled", v)}
                testid="push-pref-enabled"
              />
              <div className={`space-y-1 mt-2 pt-2 border-t border-motif ${prefs.enabled === false ? "opacity-40 pointer-events-none" : ""}`}>
                {CATEGORIES.map((c) => (
                  <ToggleRow
                    key={c.id}
                    label={T(c.label)}
                    checked={prefs[c.id] !== false}
                    onChange={(v) => setPref(c.id, v)}
                    testid={`push-pref-${c.id}`}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {env.isIOS && state !== "ios_install" && (
        <p className="text-[10px] text-motif-muted">
          {T("On iPhone, alerts require iOS 16.4+ with Venuefy installed on the Home Screen.")}
        </p>
      )}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, strong, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between py-2"
      data-testid={testid}
      role="switch"
      aria-checked={checked}
    >
      <span className={`text-sm ${strong ? "font-medium text-motif" : "text-motif"}`}>{label}</span>
      <span
        className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
        style={{ background: checked ? "var(--motif-primary)" : "var(--motif-border)" }}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform"
          style={{ transform: checked ? "translateX(18px)" : "translateX(3px)" }}
        />
      </span>
    </button>
  );
}
